import React, { useEffect, useState, useRef } from "react";
import { Info, Undo2 } from "lucide-react";
import { undoAction } from "../../utils/API";

interface SoftConfirmOperation {
  collectionName: string;
  op: string;
}

interface SoftConfirmToastProps {
  actionLogIds: string[];
  operations: SoftConfirmOperation[];
  undoWindowSeconds: number;
  onUndo: (actionLogId: string) => void;
  onDismiss: () => void;
}

const SoftConfirmToast: React.FC<SoftConfirmToastProps> = ({
  actionLogIds,
  operations,
  undoWindowSeconds,
  onUndo,
  onDismiss,
}) => {
  const [remaining, setRemaining] = useState(undoWindowSeconds);
  const [undoing, setUndoing] = useState<string | null>(null);
  const [undone, setUndone] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          onDismiss();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [onDismiss]);

  const handleUndo = async () => {
    // Undo all action logs in the batch
    setUndoing("batch");
    try {
      for (const id of actionLogIds) {
        await undoAction(id);
      }
      setUndone(true);
      actionLogIds.forEach(id => onUndo(id));
      // Auto-dismiss after showing "Undone" briefly
      setTimeout(() => onDismiss(), 1500);
    } catch {
      // If undo fails, keep the toast visible
      setUndoing(null);
    }
  };

  const opDescription = operations
    .map((op) => `${op.op} ${op.collectionName}`)
    .join(", ");

  const progressPercent = (remaining / undoWindowSeconds) * 100;

  if (undone) {
    return (
      <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
        <div className="bg-emerald-600 text-white px-5 py-2.5 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium">
          <Undo2 size={14} />
          <span>Action undone</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50"
      role="alert"
      aria-live="polite"
    >
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl px-4 py-3 min-w-[280px] max-w-[400px] overflow-hidden">
        {/* Top row: info + undo */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Info size={14} className="text-amber-500 flex-shrink-0" />
            <span className="font-medium truncate">
              Performed: <span className="capitalize">{opDescription}</span>
            </span>
          </div>
          <button
            onClick={handleUndo}
            disabled={undoing !== null}
            className="text-[#0C447C] hover:text-[#0a3a6a] font-bold text-xs whitespace-nowrap flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Undo action"
          >
            {undoing ? (
              <span className="flex items-center gap-1">
                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Undoing
              </span>
            ) : (
              <>
                <Undo2 size={12} />
                Undo
              </>
            )}
          </button>
        </div>

        {/* Countdown bar */}
        <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-400 rounded-full transition-all duration-1000 ease-linear"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <p className="mt-1 text-[9px] text-gray-400 text-right">
          {remaining}s remaining
        </p>
      </div>
    </div>
  );
};

export default SoftConfirmToast;